import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  CreateGrowReadingInput,
  GrowReading,
  ListResponse,
} from "./types";

// Grow-scoped readings: the per-grow chemistry log + saved AI analyses.
// Endpoints are nested under the grow: /v1/grows/{growId}/readings.
const base = (growID: string) => `/v1/grows/${growID}/readings`;

// useGrowReadings is the running log for a grow, newest-first.
export function useGrowReadings(growID: string | undefined) {
  return useQuery({
    queryKey: growID
      ? queryKeys.growReadings.forGrow(growID)
      : queryKeys.growReadings.forGrow("none"),
    queryFn: ({ signal }) =>
      api.get<ListResponse<GrowReading>>(base(growID!), undefined, signal),
    enabled: !!growID,
  });
}

// useLatestReading returns the most recent saved reading, or null when the
// grow has none (the server returns 200 with a null body in that case, so this
// is not an error — callers fall back to the latest refill-session state).
export function useLatestReading(growID: string | undefined) {
  return useQuery({
    queryKey: growID
      ? queryKeys.growReadings.latest(growID)
      : queryKeys.growReadings.latest("none"),
    queryFn: ({ signal }) =>
      api.get<GrowReading | null>(`${base(growID!)}/latest`, undefined, signal),
    enabled: !!growID,
  });
}

// useCreateGrowReading saves a reading (with or without an attached AI
// analysis). On success it refreshes the grow's readings log + latest, and the
// grow detail/list caches so "Current readings" and the dashboard cards pick up
// the new value. The grow audit log is invalidated too (the save writes a row
// against the grow).
export function useCreateGrowReading(growID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGrowReadingInput) =>
      api.post<GrowReading>(base(growID), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.growReadings.forGrow(growID) });
      qc.invalidateQueries({ queryKey: queryKeys.growReadings.latest(growID) });
      qc.invalidateQueries({ queryKey: queryKeys.grows.detail(growID) });
      qc.invalidateQueries({ queryKey: queryKeys.grows.all });
      qc.invalidateQueries({
        queryKey: queryKeys.auditLog.forEntity("grow", growID),
      });
    },
  });
}
